/**
 * common.js — the LEGACY formatting entry point.
 *
 * ⚠️ **New code imports utils/format.js, not this file.**
 *
 * These functions are now thin wrappers over `utils/format.js`, which owns the
 * one implementation of every format in the app. What is kept here is the
 * *empty-value contract*, and only that: these return `""` for a missing value
 * where format.js returns an em dash.
 *
 * That difference is why this file still exists rather than being deleted and
 * its imports rewritten. Roughly 200 call sites read these, and some are written
 *
 *     {formatDateIST(row.joinedAt) || "N/A"}
 *
 * — a fallback that an em dash, being truthy, would silently disable. Swapping
 * the return value under all of them at once would change what several hundred
 * cells say with nothing to catch it. A screen moves to `format.js` when it is
 * converted, and this file shrinks as they do.
 *
 * The timezone rule these established — IST for every display timestamp,
 * regardless of the viewer's browser — is unchanged and now lives in format.js,
 * documented there.
 */
import {
  formatDate,
  formatTime,
  formatDateTime,
  formatCurrency,
  formatRelative,
  DASH,
} from "./format";

/** format.js's em dash → this file's empty string. */
const blankIfMissing = (s) => (s === DASH ? "" : s);

/**
 * ⚠️ `minimumFractionDigits: 2` is not a preference here — it is what these two
 * did before, and there are ~29 files rendering money through them. format.js
 * drops the paise on a whole amount (a fees table of round numbers scans better
 * without a column of zeroes); restating the old minimum keeps every existing
 * screen byte-identical, and a screen picks up the new behaviour when it moves
 * to format.js directly.
 */
const LEGACY_PAISE = { minimumFractionDigits: 2 };

/** @deprecated use formatCurrency from utils/format */
export const formatToCurrency = (amount) => blankIfMissing(formatCurrency(amount, LEGACY_PAISE));

/** @deprecated use formatCurrency from utils/format — it already strips commas */
export const convertToIndianCurrency = (value) => blankIfMissing(formatCurrency(value, LEGACY_PAISE));

/** "23 Feb 2026" @deprecated use formatDate from utils/format */
export const formatDateIST = (dateInput) => blankIfMissing(formatDate(dateInput));

/** "02:30 PM" @deprecated use formatTime from utils/format */
export const formatTimeIST = (dateInput) => blankIfMissing(formatTime(dateInput));

/** "23 Feb 2026, 02:30 PM" @deprecated use formatDateTime from utils/format */
export const formatDateTimeIST = (dateInput) => blankIfMissing(formatDateTime(dateInput));

/** "just now" / "3d ago" @deprecated use formatRelative from utils/format */
export const timeAgoIST = (dateInput) => blankIfMissing(formatRelative(dateInput));

/**
 * "Feb 23, 2026, 2:30 PM" — month first, and an unpadded hour.
 *
 * @deprecated use formatDateTime from utils/format. Kept only because its output
 * differs from formatDateTimeIST in field order and hour padding, so the two are
 * not interchangeable at a call site without looking at the screen.
 */
export const formatToReadableDateTime = (dateInput) =>
  blankIfMissing(
    formatDateTime(dateInput, {
      month: "short", day: "numeric", year: "numeric",
      hour: "numeric", minute: "2-digit", hour12: true,
    })
  );

/**
 * ⚠️ Not a formatter and not common: the interview module's tech-stack options.
 * It is here for no reason beyond history. Left in place because moving it is a
 * rename across its call sites and belongs with that module's own work, not
 * with the formatting consolidation.
 */
export const techStacksList = ["", "JavaScript", "Java", "Spring", "Spring Boot", "Hibernate", "Kafka", "Kubernetes", "Microservices", "AWS", "Docker", "Python", "SQL", "React", "Node.js", "HTML/CSS", "JIRA", "Git", "Agile", "TypeScript", "Angular", "Machine Learning", "Data Science"];
